<template>
  <div class="dashboard-resident-evolution-list">
    <NavBar />
    <AuthBySessionExpired />

    <div class="container pt-0 mb-5">
      <!-- Header Section -->
      <div class="dashboard-resident-vital-signs-list-header">
        <div class="header-content">
          <div class="back-button-section">
            <v-btn class="btn-return" @click="handleToBack">
              <i class="fas fa-arrow-left mr-2"></i>
              Voltar
            </v-btn>
          </div>

          <div class="title-section">
            <h1 class="page-title">
              <i class="fas fa-heartbeat title-icon"></i>
              Evoluções dos Residentes
            </h1>
            <p class="page-subtitle">
              Monitoramento e histórico de evoluções multiprofissionais
            </p>
          </div>

          <!-- Legenda de Profissionais -->
          <div class="evo-professionals-count" v-if="residents.length > 0">
            <span class="evo-professionals-count__number">
              {{ typesEvolutions.length }}
            </span>
            <span class="evo-professionals-count__label">
              tipos de<br />profissionais
            </span>
          </div>
        </div>
      </div>

      <!-- ============================================
           PAINEL DE FILTROS COLAPSÁVEL
           ============================================ -->
      <section class="evo-filters-panel">
        <button
          class="evo-filters-panel__toggle"
          @click="showFilters = !showFilters"
          :aria-expanded="showFilters"
          aria-controls="filters-content"
        >
          <i class="fas fa-sliders-h evo-filters-panel__toggle-icon"></i>
          <span>Filtros</span>
          <span class="evo-filters-panel__badge" v-if="activeFiltersCount > 0">
            {{ activeFiltersCount }}
          </span>
          <i
            class="fas evo-filters-panel__chevron"
            :class="showFilters ? 'fa-chevron-up' : 'fa-chevron-down'"
          ></i>
        </button>

        <transition name="evo-slide">
          <div
            v-show="showFilters"
            id="filters-content"
            class="evo-filters-panel__content"
          >
            <div class="evo-filters-grid">
              <!-- Filtro: Pesquisa por Nome -->
              <div class="evo-filter-item evo-filter-item--search">
                <label class="evo-filter-item__label" for="filter-name">
                  <i class="fas fa-search"></i> Pesquisar
                </label>
                <v-text-field
                  id="filter-name"
                  dense
                  placeholder="Digite o nome do residente"
                  label="Nome do residente"
                  outlined
                  v-model="name"
                  @input="getResidents"
                  hide-details
                  clearable
                />
              </div>

              <!-- Filtro: Gênero -->
              <div class="evo-filter-item">
                <label class="evo-filter-item__label" for="filter-gender">
                  <i class="fas fa-venus-mars"></i> Gênero
                </label>
                <v-select
                  id="filter-gender"
                  dense
                  :items="genders"
                  v-model="gender"
                  label="Gênero"
                  outlined
                  @change="getResidents"
                  hide-details
                />
              </div>

              <!-- Filtro: Grau de Dependência -->
              <div class="evo-filter-item">
                <label class="evo-filter-item__label" for="filter-degree">
                  <i class="fas fa-user-shield"></i> Dependência
                </label>
                <v-select
                  id="filter-degree"
                  dense
                  :items="typesFilterByGrau"
                  v-model="degree_of_dependence_id"
                  label="Grau de dependência"
                  outlined
                  @change="getResidents"
                  hide-details
                />
              </div>

              <!-- Filtro: Período -->
              <div class="evo-filter-item">
                <label class="evo-filter-item__label" for="filter-period">
                  <i class="fas fa-calendar-alt"></i> Período
                </label>
                <v-select
                  id="filter-period"
                  @input="getResidents"
                  label="Período"
                  v-model="next_or_previous"
                  :items="[
                    { value: 'PREV', text: 'Datas anteriores' },
                    { value: 'NEXT', text: 'Próximas datas' },
                  ]"
                  dense
                  outlined
                  item-value="value"
                  item-text="text"
                  hide-details
                />
              </div>

              <!-- Filtro: Data -->
              <div class="evo-filter-item">
                <label class="evo-filter-item__label" for="filter-date">
                  <i class="fas fa-calendar-day"></i> Data
                </label>
                <v-text-field
                  id="filter-date"
                  label="Data de referência"
                  outlined
                  dense
                  type="date"
                  v-model="date"
                  @input="getResidents"
                  hide-details
                />
              </div>
            </div>

            <!-- Botão Limpar Filtros -->
            <div
              class="evo-filters-panel__actions"
              v-if="activeFiltersCount > 0"
            >
              <button class="evo-btn-clear-filters" @click="clearFilters">
                <i class="fas fa-times-circle"></i> Limpar filtros
              </button>
            </div>
          </div>
        </transition>
      </section>

      <!-- ============================================
           ÁREA DE CONTEÚDO PRINCIPAL
           ============================================ -->
      <main class="evo-main-content">
        <b-overlay :show="loading" opacity="0.85" rounded="lg">
          <template #overlay>
            <div class="evo-loading-state">
              <div class="evo-loading-state__spinner"></div>
              <p class="evo-loading-state__text">Carregando evoluções...</p>
            </div>
          </template>

          <!-- Lista de Residentes -->
          <div class="evo-residents-list" v-if="residents.length > 0">
            <article
              v-for="resident in residents"
              :key="resident.id"
              class="evo-resident-card"
            >
              <!-- Cabeçalho do Card do Residente -->
              <header class="evo-resident-card__header">
                <div class="evo-resident-card__avatar-container">
                  <img
                    class="evo-resident-card__avatar"
                    :src="getResidentPhoto(resident)"
                    :alt="`Foto de ${resident.name}`"
                  />
                </div>

                <div class="evo-resident-card__info">
                  <h2 class="evo-resident-card__name">
                    {{ resident.name }}
                  </h2>
                  <div class="evo-resident-card__meta">
                    <span
                      class="evo-resident-card__age"
                      v-if="resident.date_of_birth"
                    >
                      <i class="fas fa-birthday-cake"></i>
                      {{ getAge(resident.date_of_birth) }} anos
                    </span>
                    <span
                      class="evo-resident-card__evolutions-count"
                      v-if="countTotalEvolutions(resident) > 0"
                    >
                      <i class="fas fa-file-medical-alt"></i>
                      {{ countTotalEvolutions(resident) }} evoluções
                    </span>
                  </div>
                </div>

                <button
                  class="evo-resident-card__action-btn"
                  @click="handleToResident(resident.id)"
                  :aria-label="`Acessar prontuário de ${resident.name}`"
                >
                  <span>Acessar Residente</span>
                  <i class="fas fa-external-link-alt"></i>
                </button>
              </header>

              <!-- Corpo do Card: Tabela de Evoluções -->
              <div class="evo-resident-card__body">
                <!-- Verificar se há dados -->
                <div
                  v-if="!hasEvolutionsData(resident)"
                  class="evo-empty-evolution"
                >
                  <i class="fas fa-clipboard-list"></i>
                  <p>Nenhuma evolução registrada no período selecionado</p>
                </div>

                <!-- Tabela de Evoluções -->
                <div v-else class="evo-table-wrapper">
                  <table class="evo-table">
                    <thead>
                      <tr>
                        <th class="evo-table__header-type">
                          <i class="fas fa-user-md"></i>
                          Profissional
                        </th>
                        <th
                          v-for="item in resident.evolutions"
                          :key="item.date"
                          class="evo-table__header-date"
                        >
                          <span class="evo-date-header">
                            <span class="evo-date-header__day">
                              {{ formatDateDay(item.date) }}
                            </span>
                            <span class="evo-date-header__month">
                              {{ formatDateMonth(item.date) }}
                            </span>
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="typeEvolution in typesEvolutions"
                        :key="typeEvolution.id"
                        class="evo-table__row"
                        :class="{
                          'evo-table__row--has-data': hasTypeData(
                            resident,
                            typeEvolution.type
                          ),
                        }"
                      >
                        <!-- Coluna: Tipo de Profissional -->
                        <td class="evo-table__cell-type">
                          <span class="evo-professional-type">
                            <i
                              :class="getProfessionalIcon(typeEvolution.type)"
                              class="evo-professional-type__icon"
                            ></i>
                            <span class="evo-professional-type__name">
                              {{ typeEvolution.name }}
                            </span>
                          </span>
                        </td>

                        <!-- Colunas: Valores por Data -->
                        <td
                          v-for="(evolution, key) in resident.evolutions"
                          :key="key"
                          class="evo-table__cell-value"
                        >
                          <div class="evo-cell-content">
                            <template
                              v-for="(item, index) in evolution.evolutions"
                            >
                              <button
                                v-if="item.type == typeEvolution.type"
                                :key="index"
                                class="evo-evolution-btn"
                                @click="openEvolutionModal(item, resident)"
                                :title="`Clique para ver evolução completa`"
                              >
                                <span class="evo-evolution-btn__avatar">
                                  {{ getInitials(item.user_name) }}
                                </span>
                                <span class="evo-evolution-btn__info">
                                  <span class="evo-evolution-btn__name">
                                    {{ truncateName(item.user_name) }}
                                  </span>
                                  <span class="evo-evolution-btn__time">
                                    {{ formatTime(item.created_at) }}
                                  </span>
                                </span>
                                <i
                                  class="fas fa-expand-alt evo-evolution-btn__expand"
                                ></i>
                              </button>
                            </template>
                            <span
                              v-if="
                                !hasEvolutionForType(
                                  evolution,
                                  typeEvolution.type
                                )
                              "
                              class="evo-no-data"
                            >
                              —
                            </span>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </article>
          </div>

          <!-- Estado Vazio -->
          <div v-else class="evo-empty-state">
            <div class="evo-empty-state__icon">
              <i class="fas fa-users-slash"></i>
            </div>
            <h3 class="evo-empty-state__title">Nenhum residente encontrado</h3>
            <p class="evo-empty-state__description">
              Tente ajustar os filtros ou verificar os critérios de busca.
            </p>
            <button
              v-if="activeFiltersCount > 0"
              class="evo-empty-state__action"
              @click="clearFilters"
            >
              <i class="fas fa-redo"></i> Limpar filtros
            </button>
          </div>
        </b-overlay>

        <!-- Mensagens de Erro -->
        <ErrorsMessages :errors="errors" />
      </main>

      <!-- ============================================
           PAGINAÇÃO
           ============================================ -->
      <nav
        v-if="residents.length > 0"
        class="evo-pagination-container"
        aria-label="Navegação de páginas"
      >
        <b-pagination
          align="center"
          @input="getResidents()"
          v-model="page"
          :total-rows="total"
          :per-page="itemsPerPage"
          first-number
          last-number
        />
        <p class="evo-pagination-info">
          Exibindo {{ residents.length }} de {{ total }} residentes
        </p>
      </nav>
    </div>

    <!-- ============================================
         MODAL DE EVOLUÇÃO
         ============================================ -->
    <!-- :title="modalTitle" -->
    <b-modal
      id="evolution-detail-modal"
      v-model="showEvolutionModal"
      size="lg"
      centered
      hide-footer
      :title="modalTitle"
      modal-class="evo-modal"
      header-class="evo-modal__header"
      body-class="evo-modal__body"
      no-close-on-backdrop
    >
      <template #modal-header="{ close }">
        <div class="evo-modal__header-content">
          <div class="evo-modal__header-info">
            <div class="evo-modal__header-type">
              <i
                :class="
                  selectedEvolution
                    ? getProfessionalIcon(selectedEvolution.type)
                    : ''
                "
                class="evo-modal__header-icon"
              ></i>
              <span>
                {{ selectedEvolution ? selectedEvolution.evolution_type : "" }}
              </span>
            </div>
            <h5 class="evo-modal__header-title">
              Evolução de {{ selectedResident ? selectedResident.name : "" }}
            </h5>
          </div>
          <button class="evo-modal__close-btn" @click="close()">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </template>

      <div class="evo-modal__content" v-if="selectedEvolution">
        <!-- Metadados da Evolução -->
        <div class="evo-modal__meta">
          <div class="evo-modal__meta-item">
            <i class="fas fa-user"></i>
            <div class="evo-modal__meta-info">
              <span class="evo-modal__meta-label">Profissional</span>
              <span class="evo-modal__meta-value">
                {{ selectedEvolution.user_name || "Não informado" }}
              </span>
            </div>
          </div>

          <div class="evo-modal__meta-item">
            <i class="fas fa-calendar"></i>
            <div class="evo-modal__meta-info">
              <span class="evo-modal__meta-label">Data do Registro</span>
              <span class="evo-modal__meta-value">
                {{ formatDate(selectedEvolution.created_at) }}
              </span>
            </div>
          </div>

          <div class="evo-modal__meta-item">
            <i class="fas fa-clock"></i>
            <div class="evo-modal__meta-info">
              <span class="evo-modal__meta-label">Horário</span>
              <span class="evo-modal__meta-value">
                {{ formatFullTime(selectedEvolution.created_at) }}
              </span>
            </div>
          </div>
        </div>

        <div class="evo-modal__meta">
          <div class="evo-modal__text-section">
            <h6 class="evo-modal__text-title">
              <i class="fas fa-file-alt"></i>
              Conteúdo da Evolução
            </h6>
            <div class="evo-modal__text-content">
              <p v-if="selectedEvolution.text">
                {{ selectedEvolution.text }}
              </p>
              <p v-else class="evo-modal__no-text">
                Nenhum texto registrado para esta evolução.
              </p>
            </div>
          </div>

          <div
            v-if="selectedEvolution.evolution_complements.length > 0"
            class="evo-modal__text-section"
          >
            <h6 class="evo-modal__text-title">
              <i class="fas fa-comment-dots"></i>
              Complementos da Evolução
            </h6>

            <span
              v-for="complement in selectedEvolution.evolution_complements"
              :key="complement.id"
              class="tooltip-item"
            >
              {{ complement.text }}
            </span>
          </div>
        </div>

        <!-- Ações do Modal -->
        <div class="evo-modal__actions">
          <button
            class="btn-modern btn-modern-add"
            @click="copyToClipboard(selectedEvolution.text)"
            :disabled="!selectedEvolution.text"
          >
            <i class="fas fa-copy"></i>
            Copiar Texto
          </button>
        </div>
      </div>
    </b-modal>

    <!-- Toast de Feedback -->
    <transition name="evo-toast">
      <div v-if="showToast" class="evo-toast" :class="toastType">
        <i :class="toastIcon"></i>
        <span>{{ toastMessage }}</span>
      </div>
    </transition>
  </div>
</template>

<script>
import NavBar from "../templates/NavBar";
import AuthBySessionExpired from "../templates/Components/AuthBySessionExpired";
import ErrorsMessages from "../templates/Components/ErrorsMessages";
import api from "../services/api";
import { getAge } from "../lib/getAgeByDate";
import routes from "../routes";
import permission from "../lib/permission";
import { userData } from "../lib/functions";
import { formatDate } from "../lib/format";
import moment from "moment";

export default {
  name: "ResidentsEvolutionsList",

  components: {
    NavBar,
    AuthBySessionExpired,
    ErrorsMessages,
  },

  data() {
    return {
      // Controle de UI
      showFilters: true,

      // Modal
      showEvolutionModal: false,
      selectedEvolution: null,
      selectedResident: null,

      // Toast
      showToast: false,
      toastMessage: "",
      toastType: "",
      toastIcon: "",

      // Paginação
      page: 1,
      total: 0,
      itemsPerPage: 10,

      // Filtros
      next_or_previous: "PREV",
      date: moment().format("YYYY-MM-DD"),
      name: "",
      gender: "NONE",
      filterCare: "NONE",
      degree_of_dependence_id: "NONE",

      // Tipos de Evoluções
      typesEvolutions: [],

      // Opções de Filtro
      typesFilterByGrau: [
        { value: "NONE", text: "Todos" },
        { value: "GRAU1", text: "Grau 1" },
        { value: "GRAU2", text: "Grau 2" },
        { value: "GRAU3", text: "Grau 3" },
      ],

      genders: [
        { value: "NONE", text: "Todos" },
        { value: "FEMININO", text: "Feminino" },
        { value: "MASCULINO", text: "Masculino" },
      ],

      // Dados
      filter: [],
      residents: [],
      user: {},

      // Estado
      loading: false,
      errors: [],
    };
  },

  computed: {
    /**
     * Conta quantos filtros estão ativos
     */
    activeFiltersCount() {
      let count = 0;
      if (this.name) count++;
      if (this.gender !== "NONE") count++;
      if (this.degree_of_dependence_id !== "NONE") count++;
      return count;
    },

    /**
     * Título dinâmico do modal
     */
    modalTitle() {
      if (!this.selectedEvolution) {
        return "Detalhes da Evolução";
      } else {
        return `Evolução - ${this.selectedEvolution.evolution_type}`;
      }
    },
  },

  created() {
    this.retrieve();
  },

  methods: {
    userData,
    permission,
    getAge,
    formatDate,

    handleToBack: () => routes.go(-1),

    // ============================================
    // MÉTODOS DO MODAL
    // ============================================

    /**
     * Abre o modal com os detalhes da evolução
     */
    openEvolutionModal(evolution, resident) {
      this.selectedEvolution = evolution;
      this.selectedResident = resident;
      this.showEvolutionModal = true;
    },

    /**
     * Fecha o modal
     */
    closeEvolutionModal() {
      this.showEvolutionModal = false;
      this.selectedEvolution = null;
      this.selectedResident = null;
    },

    /**
     * Copia o texto da evolução para a área de transferência
     */
    async copyToClipboard(text) {
      if (!text) return;

      try {
        await navigator.clipboard.writeText(text);
        this.showToastMessage("Texto copiado com sucesso!", "success");
      } catch (err) {
        // Fallback para navegadores mais antigos
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        this.showToastMessage("Texto copiado com sucesso!", "success");
      }
    },

    async getTypesEvolutions() {
      try {
        this.loading = true;

        const response = await api.get(
          "/rest-home/evolutions/settings/actives"
        );

        const { evolution_types } = response.data;

        this.typesEvolutions = evolution_types;

        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.log(error);
      }
    },

    /**
     * Exibe mensagem toast
     */
    showToastMessage(message, type = "info") {
      this.toastMessage = message;
      this.toastType = `evo-toast--${type}`;
      this.toastIcon =
        type === "success" ? "fas fa-check-circle" : "fas fa-info-circle";
      this.showToast = true;

      setTimeout(() => {
        this.showToast = false;
      }, 3000);
    },

    // ============================================
    // MÉTODOS DE FORMATAÇÃO
    // ============================================

    /**
     * Formata o dia da data
     */
    formatDateDay(dateStr) {
      return moment(dateStr).format("DD");
    },

    /**
     * Formata o mês da data
     */
    formatDateMonth(dateStr) {
      return moment(dateStr).format("MMM").toUpperCase();
    },

    /**
     * Formata apenas o horário
     */
    formatTime(dateStr) {
      return moment(dateStr).format("HH:mm");
    },

    /**
     * Formata horário completo
     */
    formatFullTime(dateStr) {
      return moment(dateStr).format("HH:mm:ss");
    },

    /**
     * Retorna as iniciais do nome
     */
    getInitials(name) {
      if (!name) return "?";
      const parts = name.split(" ");
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (
        parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
      ).toUpperCase();
    },

    /**
     * Trunca o nome para exibição
     */
    truncateName(name) {
      if (!name) return "Não informado";
      const parts = name.split(" ");
      if (parts.length <= 2) return name;
      return `${parts[0]} ${parts[parts.length - 1]}`;
    },

    /**
     * Retorna o ícone do profissional
     */
    getProfessionalIcon(type) {
      const icons = {
        ASSISTENTE_SOCIAL: "fas fa-hands-helping",
        CUIDADOR: "fas fa-hand-holding-heart",
        DENTISTA: "fas fa-tooth",
        EDUCADORES_E_PEDAGOGOS: "fas fa-chalkboard-teacher",
        EDUCADOR_FISICO: "fas fa-running",
        ENFERMEIRO: "fas fa-user-nurse",
        FISIOTERAPEUTA: "fas fa-walking",
        FONOAUDIOLOGO: "fas fa-comment-medical",
        MEDICO: "fas fa-user-md",
        MUSICOTERAPIA: "fas fa-music",
        NUTRICIONISTA: "fas fa-apple-alt",
        PSICOLOGO: "fas fa-brain",
        RESPONSAVEL_TECNICO: "fas fa-user-tie",
        TERAPEUTA_OCUPACIONAL: "fas fa-puzzle-piece",
      };
      return icons[type] || "fas fa-list";
    },

    // ============================================
    // MÉTODOS AUXILIARES
    // ============================================

    /**
     * Retorna a URL da foto do residente
     */
    getResidentPhoto(resident) {
      if (this.permission("read", "resident_photo") && resident.photo_url) {
        return resident.photo_url;
      }
      return require("../assets/user.png");
    },

    /**
     * Verifica se o residente possui evoluções
     */
    hasEvolutionsData(resident) {
      if (!resident.evolutions || resident.evolutions.length === 0) {
        return false;
      }
      return resident.evolutions.some(
        (e) => e.evolutions && e.evolutions.length > 0
      );
    },

    /**
     * Verifica se há evolução para um tipo específico em uma data
     */
    hasEvolutionForType(evolution, type) {
      if (!evolution.evolutions) return false;
      return evolution.evolutions.some((e) => e.type === type);
    },

    /**
     * Verifica se o residente tem dados para um tipo específico
     */
    hasTypeData(resident, type) {
      if (!resident.evolutions) return false;
      return resident.evolutions.some(
        (e) => e.evolutions && e.evolutions.some((ev) => ev.type === type)
      );
    },

    /**
     * Conta o total de evoluções do residente
     */
    countTotalEvolutions(resident) {
      if (!resident.evolutions) return 0;
      return resident.evolutions.reduce((total, e) => {
        return total + (e.evolutions ? e.evolutions.length : 0);
      }, 0);
    },

    /**
     * Limpa todos os filtros
     */
    clearFilters() {
      this.name = "";
      this.gender = "NONE";
      this.degree_of_dependence_id = "NONE";
      this.page = 1;
      this.getResidents();
    },

    // ============================================
    // MÉTODOS DE DADOS
    // ============================================

    async retrieve() {
      const { user } = this.userData();
      this.user = user;
      this.getResidents();
      this.getTypesEvolutions();
    },

    async getResidents() {
      try {
        this.loading = true;
        this.residents = [];

        const params = {
          page: this.page,
          itemsPerPage: this.itemsPerPage,
          name: this.name,
          degree_of_dependence_id: this.degree_of_dependence_id,
          gender: this.gender,
          next_or_previous: this.next_or_previous,
          date: this.date,
        };

        const response = await api.get(
          "/rest-home/resident/dashboard/evolutions-list",
          { params }
        );

        const { residents, error, errors, total } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.filter = residents;
          this.residents = residents;
          this.total = total;
          this.errors = [];
        }
      } catch (error) {
        console.error("Erro ao carregar residentes:", error);
        this.errors = ["Erro ao carregar dados. Tente novamente."];
      } finally {
        this.loading = false;
      }
    },

    handleToResident(id) {
      let route = routes.resolve({
        name: "resident",
        params: { id },
        query: {
          tab: "check",
          nav: "check",
        },
      });
      window.open(route.href, "_blank");
    },
  },
};
</script>

<style scoped>
/* ============================================
   VARIÁVEIS CSS LOCAIS
   ============================================ */
.dashboard-resident-evolution-list {
  --evo-primary: #33495e;
  --evo-primary-light: #4a6785;
  --evo-primary-dark: #243342;
  --evo-accent: #0f65b0;
  --evo-accent-light: #e8f4fc;
  --evo-success: #28a745;
  --evo-success-light: #d4edda;
  --evo-warning: #ffc107;
  --evo-warning-light: #fff3cd;
  --evo-danger: #dc3545;
  --evo-danger-light: #f8d7da;
  --evo-neutral: #6c757d;
  --evo-neutral-light: #e9ecef;
  --evo-bg: #f8f9fa;
  --evo-bg-dark: #eef1f4;
  --evo-border: #dee2e6;
  --evo-text: #212529;
  --evo-text-muted: #6c757d;
  --evo-radius: 12px;
  --evo-radius-sm: 6px;
  --evo-radius-lg: 16px;
  --evo-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  --evo-shadow-md: 0 4px 12px rgba(0, 0, 0, 0.1);
  --evo-shadow-lg: 0 8px 24px rgba(0, 0, 0, 0.12);
  --evo-transition: 0.2s ease;
  --evo-transition-slow: 0.3s ease;
}

/* Header Section */
.dashboard-resident-vital-signs-list-header {
  padding: 24px 0;
  background: white;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
}

.header-content {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 24px;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
}

.title-icon {
  color: #0f65b0;
  margin-right: 12px;
}

.page-subtitle {
  color: #64748b;
  margin: 4px 0 0 0;
  font-size: 16px;
}

.evo-professionals-count {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.15);
  border-radius: var(--evo-radius-sm);
  color: #000;
}

.evo-professionals-count__number {
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1;
}

.evo-professionals-count__label {
  font-size: 0.75rem;
  line-height: 1.2;
  opacity: 0.9;
}

/* ============================================
   PAINEL DE FILTROS
   ============================================ */
.evo-filters-panel {
  margin-bottom: 1.5rem;
  background: #fff;
  border-radius: var(--evo-radius);
  box-shadow: var(--evo-shadow);
  overflow: hidden;
}

.evo-filters-panel__toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 1rem 1.25rem;
  background: #fff;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  color: var(--evo-primary);
  transition: background var(--evo-transition);
}

.evo-filters-panel__toggle:hover {
  background: var(--evo-bg);
}

.evo-filters-panel__toggle-icon {
  font-size: 1.1rem;
}

.evo-filters-panel__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  background: var(--evo-accent);
  color: #fff;
  border-radius: 11px;
  font-size: 0.75rem;
  font-weight: 600;
}

.evo-filters-panel__chevron {
  margin-left: auto;
  font-size: 0.9rem;
  transition: transform var(--evo-transition);
}

.evo-filters-panel__content {
  padding: 0 1.25rem 1.25rem;
  border-top: 1px solid var(--evo-border);
}

.evo-filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  padding-top: 1rem;
}

.evo-filter-item {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.evo-filter-item--search {
  grid-column: span 2;
}

.evo-filter-item__label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--evo-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.evo-filter-item__label i {
  font-size: 0.9rem;
  color: var(--evo-accent);
}

.evo-filters-panel__actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid var(--evo-border);
  margin-top: 1rem;
}

.evo-btn-clear-filters {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: transparent;
  border: 1px solid var(--evo-danger);
  border-radius: var(--evo-radius-sm);
  color: var(--evo-danger);
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--evo-transition);
}

.evo-btn-clear-filters:hover {
  background: var(--evo-danger);
  color: #fff;
}

/* Animação de slide */
.evo-slide-enter-active,
.evo-slide-leave-active {
  transition: all 0.3s ease;
  max-height: 500px;
  overflow: hidden;
}

.evo-slide-enter,
.evo-slide-leave-to {
  max-height: 0;
  opacity: 0;
}

/* ============================================
   CONTEÚDO PRINCIPAL
   ============================================ */
.evo-main-content {
  min-height: 400px;
}

.evo-loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
}

.evo-loading-state__spinner {
  width: 48px;
  height: 48px;
  border: 4px solid var(--evo-border);
  border-top-color: var(--evo-accent);
  border-radius: 50%;
  animation: evo-spin 1s linear infinite;
}

@keyframes evo-spin {
  to {
    transform: rotate(360deg);
  }
}

.evo-loading-state__text {
  margin-top: 1rem;
  color: var(--evo-text-muted);
  font-size: 0.95rem;
}

/* ============================================
   CARDS DE RESIDENTES
   ============================================ */
.evo-residents-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.evo-resident-card {
  background: #fff;
  border-radius: var(--evo-radius);
  box-shadow: var(--evo-shadow);
  overflow: hidden;
  transition: box-shadow var(--evo-transition);
}

.evo-resident-card:hover {
  box-shadow: var(--evo-shadow-lg);
}

.evo-resident-card__header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: linear-gradient(to right, var(--evo-bg), #fff);
  border-bottom: 1px solid var(--evo-border);
}

.evo-resident-card__avatar-container {
  flex-shrink: 0;
}

.evo-resident-card__avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #fff;
  box-shadow: var(--evo-shadow);
}

.evo-resident-card__info {
  flex: 1;
  min-width: 0;
}

.evo-resident-card__name {
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--evo-primary);
  margin: 0 0 0.25rem;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.evo-resident-card__meta {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.85rem;
  color: var(--evo-text-muted);
  flex-wrap: wrap;
}

.evo-resident-card__age,
.evo-resident-card__evolutions-count {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.evo-resident-card__age i,
.evo-resident-card__evolutions-count i {
  color: var(--evo-accent);
}

.evo-resident-card__evolutions-count {
  padding: 0.2rem 0.6rem;
  background: var(--evo-accent-light);
  border-radius: var(--evo-radius-sm);
  color: var(--evo-accent);
  font-weight: 500;
}

.evo-resident-card__action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.25rem;
  background: var(--evo-primary);
  color: #fff;
  border: none;
  border-radius: var(--evo-radius-sm);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--evo-transition);
  white-space: nowrap;
}

.evo-resident-card__action-btn:hover {
  background: var(--evo-primary-light);
  transform: translateY(-2px);
  box-shadow: var(--evo-shadow);
}

.evo-resident-card__body {
  padding: 1rem;
}

.evo-empty-evolution {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: var(--evo-text-muted);
  text-align: center;
}

.evo-empty-evolution i {
  font-size: 2.5rem;
  margin-bottom: 0.75rem;
  opacity: 0.5;
}

.evo-empty-evolution p {
  margin: 0;
  font-size: 0.95rem;
}

/* ============================================
   TABELA DE EVOLUÇÕES
   ============================================ */
.evo-table-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: var(--evo-radius-sm);
  border: 1px solid var(--evo-border);
}

.evo-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.evo-table th,
.evo-table td {
  padding: 0.6rem 0.5rem;
  text-align: center;
  border: 1px solid var(--evo-border);
}

.evo-table thead {
  background: var(--evo-bg);
}

.evo-table__header-type {
  position: sticky;
  left: 0;
  background: var(--evo-primary);
  color: #fff;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  min-width: 160px;
  z-index: 2;
  text-align: left;
  padding-left: 1rem;
}

.evo-table__header-type i {
  margin-right: 0.5rem;
}

.evo-table__header-date {
  min-width: 100px;
  background: var(--evo-bg);
}

.evo-date-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.1rem;
}

.evo-date-header__day {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--evo-primary);
}

.evo-date-header__month {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--evo-text-muted);
  text-transform: uppercase;
}

.evo-table__row {
  transition: background var(--evo-transition);
}

.evo-table__row:nth-child(even) {
  background: rgba(0, 0, 0, 0.02);
}

.evo-table__row--has-data {
  background: var(--evo-accent-light) !important;
}

.evo-table__row--has-data:nth-child(even) {
  background: #dbedf8 !important;
}

.evo-table__cell-type {
  position: sticky;
  left: 0;
  background: #fff;
  text-align: left;
  padding-left: 0.75rem;
  z-index: 1;
}

.evo-table__row:nth-child(even) .evo-table__cell-type {
  background: #fafafa;
}

.evo-table__row--has-data .evo-table__cell-type {
  background: var(--evo-accent-light) !important;
}

.evo-table__row--has-data:nth-child(even) .evo-table__cell-type {
  background: #dbedf8 !important;
}

.evo-professional-type {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.evo-professional-type__icon {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--evo-primary);
  color: #fff;
  border-radius: 50%;
  padding: 5px;
  font-size: 0.75rem;
}

.evo-professional-type__name {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--evo-text);
}

.evo-table__cell-value {
  min-width: 100px;
  vertical-align: middle;
}

.evo-cell-content {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  align-items: center;
}

.evo-no-data {
  color: var(--evo-text-muted);
  font-size: 1.2rem;
}

/* Botão de Evolução */
.evo-evolution-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.6rem;
  background: #fff;
  border: 1px solid var(--evo-border);
  border-radius: var(--evo-radius-sm);
  cursor: pointer;
  transition: all var(--evo-transition);
  min-width: 120px;
  text-align: left;
}

.evo-evolution-btn:hover {
  border-color: var(--evo-accent);
  box-shadow: var(--evo-shadow);
  transform: translateY(-2px);
}

.evo-evolution-btn:hover .evo-evolution-btn__expand {
  opacity: 1;
  color: var(--evo-accent);
}

.evo-evolution-btn__avatar {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--evo-accent);
  color: #fff;
  border-radius: 50%;
  font-size: 0.65rem;
  font-weight: 600;
  flex-shrink: 0;
}

.evo-evolution-btn__info {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.evo-evolution-btn__name {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--evo-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.evo-evolution-btn__time {
  font-size: 0.65rem;
  color: var(--evo-text-muted);
}

.evo-evolution-btn__expand {
  font-size: 0.75rem;
  color: var(--evo-text-muted);
  opacity: 0;
  transition: all var(--evo-transition);
}

/* ============================================
   ESTADO VAZIO
   ============================================ */
.evo-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  background: #fff;
  border-radius: var(--evo-radius);
  box-shadow: var(--evo-shadow);
}

.evo-empty-state__icon {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--evo-neutral-light);
  border-radius: 50%;
  margin-bottom: 1.5rem;
}

.evo-empty-state__icon i {
  font-size: 2.5rem;
  color: var(--evo-text-muted);
}

.evo-empty-state__title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--evo-text);
  margin: 0 0 0.5rem;
}

.evo-empty-state__description {
  font-size: 0.95rem;
  color: var(--evo-text-muted);
  margin: 0 0 1.5rem;
  max-width: 320px;
}

.evo-empty-state__action {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.25rem;
  background: var(--evo-primary);
  color: #fff;
  border: none;
  border-radius: var(--evo-radius-sm);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: background var(--evo-transition);
}

.evo-empty-state__action:hover {
  background: var(--evo-primary-light);
}

/* ============================================
   PAGINAÇÃO
   ============================================ */
.evo-pagination-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  margin-top: 2rem;
  padding: 1rem;
  background: #fff;
  border-radius: var(--evo-radius);
  box-shadow: var(--evo-shadow);
}

.evo-pagination-info {
  font-size: 0.85rem;
  color: var(--evo-text-muted);
  margin: 0;
}

/* ============================================
   MODAL DE EVOLUÇÃO
   ============================================ */
.evo-modal__header {
  background: linear-gradient(
    135deg,
    var(--evo-primary) 0%,
    var(--evo-primary-light) 100%
  ) !important;
  color: #fff !important;
  padding: 0 !important;
  border: none !important;
}

.evo-modal__header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 1rem 1.5rem;
}

.evo-modal__header-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.evo-modal__header-type {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  opacity: 0.9;
}

.evo-modal__header-icon {
  font-size: 1rem;
}

.evo-modal__header-title {
  font-size: 1.15rem;
  font-weight: 600;
  margin: 0;
  color: #fff;
}

.evo-modal__close-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.15);
  border: none;
  border-radius: 50%;
  color: #fff;
  cursor: pointer;
  transition: background var(--evo-transition);
}

.evo-modal__close-btn:hover {
  background: rgba(255, 255, 255, 0.25);
}

.evo-modal__body {
  padding: 0 !important;
}

.evo-modal__content {
  padding: 1.5rem;
}

.evo-modal__meta {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  padding: 1rem;
  background: var(--evo-bg);
  border-radius: var(--evo-radius-sm);
  margin-bottom: 1.5rem;
}

.evo-modal__meta-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.evo-modal__meta-item > i {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--evo-accent);
  color: #fff;
  border-radius: 50%;
  font-size: 0.85rem;
  flex-shrink: 0;
}

.evo-modal__meta-info {
  display: flex;
  flex-direction: column;
}

.evo-modal__meta-label {
  font-size: 0.75rem;
  color: var(--evo-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.evo-modal__meta-value {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--evo-text);
}

.evo-modal__text-section {
  margin-bottom: 1.5rem;
}

.evo-modal__text-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--evo-primary);
  margin: 0 0 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--evo-accent-light);
}

.evo-modal__text-title i {
  color: var(--evo-accent);
}

.evo-modal__text-content {
  background: #fff;
  border: 1px solid var(--evo-border);
  border-radius: var(--evo-radius-sm);
  padding: 1rem;
  max-height: 300px;
  overflow-y: auto;
}

.evo-modal__text-content p {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.7;
  color: var(--evo-text);
  white-space: pre-wrap;
  word-break: break-word;
}

.evo-modal__no-text {
  color: var(--evo-text-muted);
  font-style: italic;
  text-align: center;
}

.evo-modal__actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid var(--evo-border);
}

.evo-modal__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.25rem;
  border-radius: var(--evo-radius-sm);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--evo-transition);
}

.evo-modal__btn--secondary {
  background: #fff;
  color: var(--evo-primary);
  border: 1px solid var(--evo-primary);
}

.evo-modal__btn--secondary:hover:not(:disabled) {
  background: var(--evo-bg);
}

.evo-modal__btn--secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.evo-modal__btn--primary {
  background: var(--evo-primary);
  color: #fff;
  border: none;
}

.evo-modal__btn--primary:hover {
  background: var(--evo-primary-light);
}

/* ============================================
   TOAST DE FEEDBACK
   ============================================ */
.evo-toast {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: var(--evo-primary);
  color: #fff;
  border-radius: var(--evo-radius-sm);
  box-shadow: var(--evo-shadow-lg);
  z-index: 9999;
}

.evo-toast--success {
  background: var(--evo-success);
}

.evo-toast--error {
  background: var(--evo-danger);
}

.evo-toast i {
  font-size: 1.25rem;
}

/* Animação do Toast */
.evo-toast-enter-active,
.evo-toast-leave-active {
  transition: all 0.3s ease;
}

.evo-toast-enter,
.evo-toast-leave-to {
  opacity: 0;
  transform: translateY(1rem);
}

/* ============================================
   RESPONSIVIDADE
   ============================================ */
@media (max-width: 768px) {
  .evo-page-header__content {
    flex-direction: column;
    align-items: flex-start;
  }

  .evo-page-header__title {
    font-size: 1.25rem;
  }

  .evo-page-header__subtitle {
    margin-left: 0;
  }

  .evo-professionals-count {
    align-self: flex-start;
  }

  .evo-filter-item--search {
    grid-column: span 1;
  }

  .evo-resident-card__header {
    flex-wrap: wrap;
  }

  .evo-resident-card__action-btn {
    width: 100%;
    justify-content: center;
    margin-top: 0.5rem;
  }

  .evo-table__cell-type {
    min-width: 140px;
  }

  .evo-professional-type__icon {
    width: 24px;
    height: 24px;
    font-size: 0.65rem;
  }

  .evo-professional-type__name {
    font-size: 0.75rem;
  }

  .evo-evolution-btn {
    min-width: 100px;
    padding: 0.3rem 0.4rem;
  }

  .evo-modal__meta {
    grid-template-columns: 1fr;
  }

  .evo-modal__actions {
    flex-direction: column;
  }

  .evo-modal__btn {
    width: 100%;
    justify-content: center;
  }

  .evo-toast {
    left: 1rem;
    right: 1rem;
    bottom: 1rem;
  }
}

@media (max-width: 480px) {
  .evo-filters-grid {
    grid-template-columns: 1fr;
  }

  .evo-resident-card__avatar {
    width: 48px;
    height: 48px;
  }

  .evo-resident-card__name {
    font-size: 1rem;
  }

  .evo-resident-card__meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
}
</style>